import * as React from 'react'

import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { Permissions } from 'app/constants/permissions'
import { PermissionsContextMenu } from 'app/components/administration/contextMenu'
import { useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon, CheckboxField } from '@/modules/shared/components'
import { PERMISSIONS, PERMISSION_COLORS } from '../permissions'

type Props = {
  role: RolePermissionModel
  onTogglePermission: (role: RolePermissionModel, permission: string) => void
  onDelete: (role: RolePermissionModel) => void
}

/**
 * Wiersz roli: nazwa, pigułki uprawnień i kosz.
 *
 * Uprawnienia dodaje się prawym przyciskiem (menu kontekstowe jak dotąd), a zdejmuje
 * krzyżykiem na pigułce. Role systemowe i dziedziczone są tylko do odczytu.
 */
export const RoleRow: React.FC<Props> = ({
  role,
  onTogglePermission,
  onDelete,
}) => {
  const { t } = useLocalize()
  const [menuAnchor, setMenuAnchor] = React.useState<{
    x: number
    y: number
  } | null>(null)
  const { toggleId, selectedGroup } = useTable()
  const isChecked = selectedGroup?.ids.includes(role.id) ?? false

  return (
    <tr
      className={`tw-border-b tw-border-base-300 hover:tw-bg-primary/10 ${
        isChecked ? 'tw-bg-primary/10' : ''
      }`}
    >
      <td className="tw-w-12 tw-px-4 tw-py-3">
        <CheckboxField
          id={`role-${role.id}`}
          checked={isChecked}
          disabled={role.systemRole}
          onChange={() => toggleId(role.id, true)}
        />
      </td>
      <td className="tw-w-[250px] tw-px-4 tw-py-3 tw-text-sm tw-font-medium">
        {role.name || '-'}
      </td>
      <td
        className="tw-px-4 tw-py-3"
        title={t('administrationPanel-roles.clickRightToOpenContextMenu')}
      >
        <span
          onContextMenu={(event) => {
            event.preventDefault()
            setMenuAnchor({ x: event.clientX, y: event.clientY })
          }}
        >
          {/* `min-h` + `w-full`: rola bez uprawnień ma pustą listę pigułek, a bez własnej
              wysokości nie dałoby się kliknąć prawym, żeby dodać pierwsze uprawnienie. */}
          <div className="tw-flex tw-min-h-[24px] tw-w-full tw-flex-wrap tw-items-center tw-gap-1">
            {PERMISSIONS.filter((permission) => role[permission]).map(
              (permission) => (
                <span
                  key={permission}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-text-white"
                  style={{ backgroundColor: PERMISSION_COLORS[permission] }}
                >
                  {t(`administrationPanel-roles.${permission}`)}
                  <AdminIcon
                    name="close-white"
                    className="tw-h-4 tw-w-4"
                    onClick={() => onTogglePermission(role, permission)}
                  />
                </span>
              ),
            )}
          </div>
          <PermissionsContextMenu
            id={role.id}
            anchorPoint={menuAnchor}
            onClose={() => setMenuAnchor(null)}
            translate={t}
            permissions={PERMISSIONS}
            role={role}
            onClickAction={(permission: Permissions) =>
              onTogglePermission(role, permission)
            }
          />
        </span>
      </td>
      <td className="tw-w-12 tw-px-2 tw-py-3">
        {!role.inherited && (
          <AdminIcon
            name="delete"
            title={t('operation.delete')}
            onClick={() => onDelete(role)}
          />
        )}
      </td>
    </tr>
  )
}
